import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDate } from "../helper/DateHelper";
import { useNavigate, useParams } from "react-router-dom";
import { getManagerExpoBookings } from "../Apis/ManagerAPIs";
import { getExpoById } from "../Apis/ExpoAPIs";
import { toast } from "react-toastify";
import TablePagination from "./TablePagination";

const EventManagerBookingsLayer = () => {
  const navigate = useNavigate();
  const { id, expoId } = useParams();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expoName, setExpoName] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const expoResult = await getExpoById(expoId);
      if (expoResult.status && expoResult.response?.expoName) {
        setExpoName(expoResult.response.expoName);
      }

      const params = {
        page: currentPage,
        limit: rowsPerPage,
      };

      const result = await getManagerExpoBookings(id, expoId, params);
      if (result.status) {
        const responseData = result.response?.data || result.response;
        setBookings(responseData || []);

        const total = typeof result.response?.total === 'number'
          ? result.response.total
          : (result.response?.data?.total || result.response?.total || 0);
        setTotalItems(total);
      } else {
        setBookings([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching expo bookings:", error);
      toast.error("Failed to load bookings");
      setBookings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && expoId) {
      fetchBookings();
    }
  }, [id, expoId, currentPage, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      <div
        className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
        style={{ background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)" }}
      >
        <h6 className="text-white mb-0 fw-bold">BOOKINGS FOR {expoName ? expoName.toUpperCase() : "EXHIBITION"}</h6>

        <button
          onClick={() => navigate(-1)}
          className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm transition-all hover-scale-105"
          style={{ fontSize: "12px", backgroundColor: "#f7e7da" }}
        >
          <Icon icon="solar:arrow-left-bold" />
          Back
        </button>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-48">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : bookings.length > 0 ? (
          <>
            <div className="table-responsive border radius-8 overflow-hidden shadow-sm mb-16">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr style={{ background: "linear-gradient(135deg, #bb0007, #ed1c24)" }}>
                    <th scope="col" className="fw-bold text-white py-12 px-16 text-center" style={{ width: "60px" }}>S.No</th>
                    <th scope="col" className="fw-bold text-white py-12">Client Name</th>
                    <th scope="col" className="fw-bold text-white py-12">Company Name</th>
                    <th scope="col" className="fw-bold text-white py-12">Contact</th>
                    <th scope="col" className="fw-bold text-white py-12 text-center">Stall No</th>
                    <th scope="col" className="fw-bold text-white py-12 text-center">Stall Size (Sqm)</th>
                    <th scope="col" className="fw-bold text-white py-12 text-center">Net Amount</th>
                    <th scope="col" className="fw-bold text-white py-12 text-center">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b, idx) => (
                    <tr key={b._id || idx}>
                      <td className="py-10 px-16 text-center">{currentPage * rowsPerPage + idx + 1}</td>
                      <td className="py-10 fw-semibold text-dark">{b.name}</td>
                      <td className="py-10 text-secondary-light">{b.companyName || "N/A"}</td>
                      <td className="py-10 text-secondary-light">
                        <div className="d-flex flex-column text-xs">
                          <span>{b.mobileNo}</span>
                          <span className="text-muted">{b.email}</span>
                        </div>
                      </td>
                      <td className="py-10 text-center fw-bold">
                        <span className="badge bg-danger-focus text-danger-main radius-4 px-10 py-4">
                          {b.stallNo || "N/A"}
                        </span>
                      </td>
                      <td className="py-10 text-center">{b.sqm ? `${b.sqm} Sqm` : "N/A"}</td>
                      <td className="py-10 text-center fw-bold text-success-600">₹{b.amount ? b.amount.toLocaleString('en-IN') : 0}</td>
                      <td className="py-10 text-center text-xs text-secondary-light">
                        {b.blockedDate ? formatDate(b.blockedDate) : b.createdAt ? formatDate(b.createdAt) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalRecords={totalItems}
            />
          </>
        ) : (
          <div className="text-center py-48 text-secondary-light">
            No stall bookings registered by this manager for this expo.
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManagerBookingsLayer;
